import React, { useRef, useState, useEffect } from 'react';
import HeaderNavbar from '../components/HeaderNavbar';
import Footer from '../components/Footer';
import { Alert } from 'react-bootstrap';
import Spinner from 'react-bootstrap/Spinner';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import api from '../lib/request';
import config from '../config';
import './Login.css';

const MAX_MESSAGE_LEN = 500;

function Contact() {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [reasons, setReasons] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [completed, setCompleted] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const captchaRef = useRef(null);
  const errorRef = useRef(null);
  const successRef = useRef(null);

  const validate = () => {
    const issues = [];
    const name = fullName.trim();
    const mail = email.trim();
    const subj = subject.trim();
    const msg = message.trim();
    if (!name) issues.push('Full name is required.');
    if (!mail) {
      issues.push('Email is required.');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mail)) {
      issues.push('Enter a valid email address.');
    }
    if (!subj) issues.push('Subject is required.');
    if (!msg) issues.push('Message is required.');
    if (msg && msg.length > MAX_MESSAGE_LEN) issues.push(`Message must be ${MAX_MESSAGE_LEN} characters or less.`);
    return issues;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setReasons([]);
    setSuccessMessage('');

    const issues = validate();
    if (issues.length) {
      setError('Please fix the highlighted issues and try again.');
      setReasons(issues);
      return;
    }

    const captchaEnabled = !!config.hcaptchaSiteKey;
    if (captchaEnabled && !captchaToken) {
      setError('Please complete the captcha.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        fullName: fullName.trim(),
        email: email.trim(),
        subject: subject.trim(),
        message: message.trim(),
        ...(captchaEnabled ? { hcaptchaToken: captchaToken } : {})
      };
      const response = await api.post('/api/v1/contact', payload);
      const msg = response?.message || 'Message sent. Thank you for reaching out!';
      setSuccessMessage(msg);
      setCompleted(true);
      setFullName('');
      setEmail('');
      setSubject('');
      setMessage('');
      try { captchaRef.current?.resetCaptcha(); } catch (e) {}
      setCaptchaToken('');
    } catch (err) {
      const backendReasons = Array.isArray(err?.reasons) ? err.reasons : [];
      if (backendReasons.length) setReasons(backendReasons);
      setError(err?.message || 'Unable to send your message. Please try again later.');
      try { captchaRef.current?.resetCaptcha(); } catch (e) {}
      setCaptchaToken('');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if ((error || (reasons && reasons.length > 0)) && errorRef.current) {
      try {
        errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
        if (typeof errorRef.current.focus === 'function') errorRef.current.focus();
      } catch (e) {}
    }
  }, [error, reasons]);

  useEffect(() => {
    if (completed && successRef.current) {
      try {
        successRef.current.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
        if (typeof successRef.current.focus === 'function') successRef.current.focus();
      } catch (e) {}
    }
  }, [completed]);

  return (
    <>
      <HeaderNavbar />
      <div className="row">
        <div className="Auth-form-container">
          {completed ? (
            <div className="Auth-form">
              <div className="Auth-form-content" style={{ textAlign: 'center' }}>
                <div style={{display: 'flex', justifyContent: 'center', marginBottom: 10}}>
                  <img src="/logo.svg" alt="Logo" width="100" height="100" />
                </div>
                <div ref={successRef} tabIndex={-1} aria-live="polite">
                  {successMessage && <Alert variant="success">{successMessage}</Alert>}
                </div>
              </div>
            </div>
          ) : (
            <form className="Auth-form" onSubmit={handleSubmit} noValidate>
              <div className="Auth-form-content">
                <div style={{display: 'flex', justifyContent: 'center', marginBottom: 10}}>
                  <img src="/logo.svg" alt="Logo" width="100" height="100" />
                </div>
                <h3 className="Auth-form-title">Contact us</h3>
                
                <div className="text-center mb-4">
                  <div className="text-muted text-center">We’ll get back to you by email.</div>
                </div>
                
                <div className="text-center mb-4">
                  <div className="d-inline-block text-start">
                    <div className="d-flex align-items-center">
                      <span className="badge bg-primary me-2">•</span>
                      <span>For a chat, join our <a href="https://discord.gg/jS6FkUJF4y" target="_blank" rel="noopener noreferrer" className="text-primary">Discord</a>.</span>
                    </div>
                  </div>
                </div>

                <div ref={errorRef} tabIndex={-1} aria-live="polite">
                  {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
                  {reasons.length > 0 && (
                    <Alert variant="danger" className="mt-3">
                      <ul className="mb-0" style={{ paddingLeft: '1.2rem' }}>
                        {reasons.map((r) => (
                          <li key={r}>{r}</li>
                        ))}
                      </ul>
                    </Alert>
                  )}
                </div>

                <div className="form-group mt-3">
                  <label>Full name</label>
                  <input
                    type="text"
                    className="form-control mt-1"
                    placeholder="Enter your full name"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    required
                    autoComplete="name"
                  />
                </div>

                <div className="form-group mt-3">
                  <label>Email address</label>
                  <input
                    type="email"
                    className="form-control mt-1"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                  />
                </div>

                <div className="form-group mt-3">
                  <label>Subject</label>
                  <input
                    type="text"
                    className="form-control mt-1"
                    placeholder="Subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    required
                    maxLength={200}
                  />
                </div>

                <div className="form-group mt-3">
                  <label>Message</label>
                  <textarea
                    className="form-control mt-1"
                    placeholder="How can we help?"
                    rows={6}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    required
                    maxLength={MAX_MESSAGE_LEN}
                  />
                  <div className="text-muted" style={{ fontSize: '0.85rem' }}>{message.length}/{MAX_MESSAGE_LEN}</div>
                </div>

                <div className="form-group mt-3">
                  {config.hcaptchaSiteKey ? (
                    <HCaptcha
                      sitekey={config.hcaptchaSiteKey}
                      onVerify={(token) => setCaptchaToken(token)}
                      onExpire={() => setCaptchaToken('')}
                      onError={() => setCaptchaToken('')}
                      ref={captchaRef}
                      reCaptchaCompat={false}
                    />
                  ) : (
                    <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                      Captcha is disabled. Set REACT_APP_HCAPTCHA_SITEKEY to enable.
                    </div>
                  )}
                </div>

                <div className="d-grid gap-2 mt-3">
                  {submitting ? (
                    <Spinner animation="border" />
                  ) : (
                    <button type="submit" className="btn btn-primary">Send message</button>
                  )}
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
}

export default Contact;
